import { createApi } from "@reduxjs/toolkit/query/react";
import { apiConstants } from "../common/constants";
import baseQuery from "./baseQuery";
import { setServiceabilityGroups } from './pricingWizardSlice';
import getShipmentsByParallelRequests from "./queries/parallel_shipments";
import parallelShipmentsByDate from "./queries/parallel_shipments_by_date";

export const commonApiCalls = createApi({
  reducerPath: "commonApiCalls",
  baseQuery: baseQuery,

  // cache expiry time in seconds
  keepUnusedDataFor: 2 * 30 * 60,
  tagTypes: ["ReleaseNotes", "ReleaseNotesUnread"],
  endpoints: (builer) => ({
    getAllMerchants: builer.query({
      query: () => ({
        url: apiConstants.MERCHANT_SEARCH,
        data: {
          andfilter: [{ field: "isTestMerchant", operator: "ne", value: true }],
          offset: 0,
          limit: 2000,
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getAllMerchantsIdAndName: builer.query({
      // const includes = "id,name"
      query: () => ({
        // url: apiConstants.MERCHANT_SEARCH, data: {
        url: apiConstants.MERCHANT_SEARCH_CACHED,
        data: {
          andfilter: [{ field: "isTestMerchant", operator: "ne", value: true }],
          offset: 0,
          limit: 2000,
          includes: "id,name,merchantId,is_deleted",
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getAllActiveMerchantsIdAndName: builer.query({
      // const includes = "id,name"
      query: () => ({
        url: apiConstants.MERCHANT_SEARCH,
        data: {
          andfilter: [
            { field: "isTestMerchant", operator: "ne", value: true },
            { field: "status", operator: "eq", value: "KYC_APPROVED" },
          ],
          offset: 0,
          limit: 2000,
          includes: "id,name,merchantId,is_deleted,companyName",
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getAllActiveMerchantsIdAndNameCached: builer.query({
      // const includes = "id,name"
      query: () => ({
        url: apiConstants.MERCHANT_SEARCH_CACHED,
        data: {
          andfilter: [
            { field: "isTestMerchant", operator: "ne", value: true },
            { field: "status", operator: "eq", value: "KYC_APPROVED" },
          ],
          offset: 0,
          limit: 2000,
          includes:
            "id,name,merchantId,is_deleted,isPTLMerchant,isD2CMerchant,companyName",
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getAllActiveMerchantsIdAndNameNONKYCCached: builer.query({
      // const includes = "id,name"
      query: () => ({
        url: apiConstants.MERCHANT_SEARCH_CACHED,
        data: {
          andfilter: [{ field: "isTestMerchant", operator: "ne", value: true }],
          offset: 0,
          limit: 2000,
          includes: "id,name,merchantId,is_deleted,status,companyName",
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getMerchant: builer.query({
      query: ({ id }) => ({
        url: `${apiConstants.MERCHANT}/${id}`,
        type: "GET",
      }),
    }),
    getAllActiveMerchantsIdCached2: builer.query({
      // const includes = "id,name"
      query: () => ({
        url: apiConstants.MERCHANT_SEARCH_CACHED,
        data: {
          andfilter: [
            { field: "isTestMerchant", operator: "ne", value: true },
            // { field: "status", operator: "eq", value: 'KYC_APPROVED' }
          ],
          offset: 0,
          limit: 1000,
          includes: "id,name,merchantId,is_deleted,companyName",
        },
      }),
      transformResponse: (res) => res.data,
    }),
    getAllCourierPartners: builer.query({
      query: (data) => ({
        url: apiConstants.COURIER_PARTNER_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    getAllCourierPartnersCached: builer.query({
      query: (data) => ({
        url: apiConstants.COURIER_PARTNER_SEARCH_CACHED,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    // Same request shape as getAllCourierPartnersCached, but excludes inactive couriers and
    // couriers whose parent is PTL-only — for the report Courier filter dropdown specifically.
    getReportEligibleCourierPartners: builer.query({
      query: (data) => ({
        url: apiConstants.COURIER_PARTNER_SEARCH_REPORT_ELIGIBLE,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    getPtlPur: builer.query({
      query: (data) => ({
        url: apiConstants.PTL_PUR_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    getFutwork: builer.query({
      query: (data) => ({
        url: apiConstants.PTL_PUR_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    getSearchMerchant: builer.query({
      query: (data) => ({
        url: apiConstants.MERCHANT_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => res.data,
    }),
    // Same endpoint, but keeps the total `count` so callers can drive server-side
    // pagination instead of fetching the whole list. `res` is the axios response,
    // so the body (with `count` + `data`) lives at `res.data`.
    getSearchMerchantPaged: builer.query({
      query: (data) => ({
        url: apiConstants.MERCHANT_SEARCH,
        data,
        type: "POST",
      }),
      transformResponse: (res) => {
        const body = res?.data ?? {};
        const list = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : []);
        return { data: list, count: body?.count ?? body?.total ?? list.length };
      },
    }),
    getAllReports: builer.query({
      query: (data) => ({
        url: apiConstants.GET_ALL_REPORTS,
        data,
        type: "GET",
      }),
      transformResponse: (response) => {
        return response.responseObj;
      },
    }),
    getShipmentDetail: builer.query({
      query: (data) => ({ url: apiConstants.ORDER_LISTS, data, type: "POST" }),
    }),
    getAllOrders: builer.query({
      query: (data) => ({ url: apiConstants.ORDER_SEARCH, data }),
    }),
    getAllReportTypes: builer.query({
      query: () => ({ url: apiConstants.GET_ALL_REPORT_TYPES, type: "GET" }),
      transformResponse: (response) => {
        return response.responseObj;
      },
    }),
    getReportTypeDetails: builer.query({
      query: () => ({ url: apiConstants.GET_REPORT_TYPE_DETAILS, type: "GET" }),
      transformResponse: (response) => {
        return response.responseObj;
      },
    }),
    getAllOrdersInParallel: builer.query({
      queryFn: getShipmentsByParallelRequests,
    }),
    getAllDataUsingParallelRequestsBasedOnDate: builer.query({
      queryFn: parallelShipmentsByDate,
    }),
    getChannelOrder: builer.query({
      query: (data) => ({
        url: apiConstants.CHANNEL_ORDER_SEARCh,
        data,
        type: "POST",
      }),
    }),
    genericPostRequest: builer.query({
      query: (data) => ({ ...data }),
    }),
    getNPRData: builer.query({
      query: (data) => ({
        url: apiConstants.NPR_LISTS,
        data,
        type: "POST",
      }),
    }),
    getChannelOrderCountUF: builer.query({
      query: (pay) => ({
        url: apiConstants.CHANNEL_ORDER_SEARCh,
        data: {
          andfilter: pay,
          countOnly: true,
        },
      }),
      transformResponse: (res) => res.count,
    }),
    getMetchantConfig: builer.query({
      query: ({ id }) => ({
        url: `${apiConstants.MERCHANT_CONFIG}/${id}`,
        type: "GET",
      }),
    }),
    getRuleHistory: builer.query({
      query: ({ merchantOid }) => ({
        url: `${apiConstants.RULE_HISTORY}/${merchantOid}`,
        type: "GET",
      }),
    }),
    getChannelOrderCountFU: builer.query({
      query: (pay) => {
        console.log(pay);
        return {
          url: apiConstants.CHANNEL_ORDER_SEARCh,
          data: {
            andfilter: pay,
            countOnly: true,
          },
        };
      },
      transformResponse: (res) => res.count,
    }),
    getDashboardUnprocessedTrend: builer.query({
      query: (data) => ({
        url: apiConstants.DASHBOARD_UNPROCESSED_TREND,
        data,
        type: 'GET',
      }),
    }),
    getDashboardDailyTrend: builer.query({
      query: (data) => ({
        url: apiConstants.DASHBOARD_DAILY_TREND,
        data,
        type: 'GET',
      }),
    }),
    /* ─────────────── Release Notes ─────────────── */
    getReleaseNotes: builer.query({
      query: (params = {}) => ({
        url: apiConstants.RELEASE_NOTES,
        data: { limit: params.limit ?? 50, ...(params.before ? { before: params.before } : {}) },
        type: 'GET',
      }),
      transformResponse: (res) => ({
        items: Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data) ? res.data
          : Array.isArray(res?.notes) ? res.notes
          : Array.isArray(res) ? res
          : [],
        count: res?.count ?? 0,
      }),
      providesTags: ['ReleaseNotes'],
    }),
    getReleaseNotesUnreadCount: builer.query({
      query: () => ({
        url: apiConstants.RELEASE_NOTES_UNREAD,
        type: 'GET',
      }),
      transformResponse: (res) => {
        const c = res?.count ?? res?.unreadCount ?? res ?? 0;
        return typeof c === 'number' ? c : Number(c) || 0;
      },
      providesTags: ['ReleaseNotesUnread'],
    }),
    getServiceabilityGroups: builer.query({
      query: () => ({ url: apiConstants.SERVICEABILITY_INTEL_GROUPS, type: 'GET' }),
      transformResponse: (res) => Array.isArray(res?.responseObj?.groups) ? res.responseObj.groups : [],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(setServiceabilityGroups(data));
        } catch {}
      },
    }),
    getWmsWarehouse: builer.query({
      query: () => ({ url: apiConstants.WMS_WAREHOUSE, type: 'GET' }),
      transformResponse: (res) => {
        const DEDICATED_CATEGORIES = ['DEDICATED_CPU', 'DEDICATED_COST_PLUS', 'MANAGED_CPU', 'MANAGED_COST_PLUS'];
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        return list.filter((w) => {
          const cat = w.utilizationCategory ?? '';
          if (DEDICATED_CATEGORIES.includes(cat)) return true;
          if (cat === 'MULTI_CLIENT' && w.managedBy?.toLowerCase() === 'prozo') return true;
          return false;
        });
      },
    }),
    markReleaseNotesSeen: builer.mutation({
      query: () => ({
        url: apiConstants.RELEASE_NOTES_SEEN,
        data: undefined,
        type: 'POST',
      }),
      invalidatesTags: ['ReleaseNotesUnread'],
    }),
  }),
});

export const {
  useGetAllMerchantsQuery,
  useGetAllMerchantsIdAndNameQuery,
  useGetMerchantQuery,
  useLazyGetMerchantQuery,
  useGetAllActiveMerchantsIdAndNameQuery,
  useGetAllActiveMerchantsIdAndNameCachedQuery,
  useGetAllActiveMerchantsIdAndNameNONKYCCachedQuery,
  useGetAllActiveMerchantsIdCached2Query,
  useGetAllCourierPartnersQuery,
  useGetAllCourierPartnersCachedQuery,
  useGetReportEligibleCourierPartnersQuery,
  useGetAllReportsQuery,
  useGetAllReportTypesQuery,
  useGetReportTypeDetailsQuery,
  useGetAllOrdersInParallelQuery,
  useLazyGetAllOrdersInParallelQuery,
  useGetAllOrdersQuery,
  useGetNPRDataQuery,
  useGetChannelOrderQuery,
  useLazyGetShipmentDetailQuery,
  useGetPtlPurQuery,
  useGetSearchMerchantQuery,
  useGetSearchMerchantPagedQuery,
  useGetFutworkQuery,
  useGenericPostRequestQuery,
  useLazyGenericPostRequestQuery,
  useGetAllDataUsingParallelRequestsBasedOnDateQuery,
  useGetAllShipmentsWithPickupAndDeliveryDataQuery,
  useGetChannelOrderCountFUQuery,
  useGetChannelOrderCountUFQuery,
  useGetMetchantConfigQuery,
  useGetRuleHistoryQuery,
  useGetDashboardUnprocessedTrendQuery,
  useGetDashboardDailyTrendQuery,
  useGetServiceabilityGroupsQuery,
  useGetWmsWarehouseQuery,
  useGetReleaseNotesQuery,
  useGetReleaseNotesUnreadCountQuery,
  useMarkReleaseNotesSeenMutation,
} = commonApiCalls;
